import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import {
  CommitteeCycleModel,
  StandardsBallotsVoteModel,
  StandardsBallotLineItemModel,
} from 'store/types/StandardsBallot';
import CardComponent from 'components/common/CardComponent';
import CardFieldRowComponent from 'components/common/CardFieldRowComponent';
import RadioButtonComponent, { RadioButton } from 'components/common/RadioButtonComponent';
import TextAreaComponent from 'components/common/TextAreaComponent';
import FileComponent from 'components/common/FileComponent';
import SpinnerComponent from 'components/SpinnerComponent';
import Spinner from 'components/shared/Spinner';
import { BallotTypes, ratificationOptions, technicalOptions, VotingOptions } from 'store/types/BallotTypes';
import { findLineItemVote } from 'util/ballot';
import Validators from 'util/validators';
import routes from 'store/configs/Routes';
import { HttpError } from 'services/HttpService';
import { useStandardsBallotContext } from '../StandardsBallotContext';
import { BallotsService } from 'services/BallotService';

import './StandardsBallotsFormCardComponent.scss';

interface FieldErrors {
  voteSelection?: string;
  comments?: string;
}

const StandardsBallotsFormCardComponent: React.FunctionComponent = () => {
  const { committee = '', period = '' } = useParams<{ committee: string; period: string }>();
  const navigate = useNavigate();
  const { committeeCycles, ballotDataLoading, cycleVotes, cycleVotesLoading, refetchCycleVotes } =
    useStandardsBallotContext();
  const loading = useMemo(() => ballotDataLoading || cycleVotesLoading, [ballotDataLoading, cycleVotesLoading]);

  const selectedCycle = useMemo<CommitteeCycleModel | undefined>(
    () => committeeCycles?.find((cc) => cc.committee === committee && cc.period === period),
    [committeeCycles, committee, period]
  );

  const voteMap = useMemo(() => {
    const committeeCycleVote = cycleVotes?.find((cv) => cv.committee === committee && cv.period === period);
    return new Map(
      selectedCycle?.ballotLineData.map((lineItem) => [
        lineItem.lineItemInternalId,
        findLineItemVote(lineItem, committeeCycleVote?.votedata || []),
      ]) || []
    );
  }, [cycleVotes, committee, period, selectedCycle]);

  const [editedVotes, setEditedVotes] = useState<StandardsBallotsVoteModel[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors[]>([]);
  const [spinnerVisible, setSpinnerVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const votes = Array.from(voteMap.values());
    setEditedVotes(votes);
    setFieldErrors(Array(votes.length).fill({}));
  }, [voteMap]);

  const getDocumentTitle = useCallback(
    (lineItem: StandardsBallotLineItemModel) =>
      lineItem.ballotTypeId === BallotTypes.Ratification
        ? `${lineItem.docTitle} (Ratification ballot)`
        : lineItem.docTitle,
    []
  );

  const getRadioButtonOptions = useCallback(
    (ballotTypeId: BallotTypes): RadioButton[] =>
      (ballotTypeId === BallotTypes.Ratification ? ratificationOptions : technicalOptions).map((option) => ({
        label: option,
        value: option,
      })),
    []
  );

  const areCommentsDisabled = useCallback((vote: StandardsBallotsVoteModel): boolean => {
    if (vote.ballotTypeId === BallotTypes.Technical && vote.vote) {
      return vote.vote === VotingOptions.Accept || vote.vote === VotingOptions.Abstain;
    }
    if (vote.ballotTypeId === BallotTypes.Ratification && vote.vote) {
      return vote.vote !== VotingOptions.Disapprove;
    }
    return true;
  }, []);

  const updateVote = useCallback((lineItem: StandardsBallotLineItemModel, newVote: StandardsBallotsVoteModel) => {
    setEditedVotes((prev) =>
      prev.map((v) =>
        lineItem.ballotInternalId === v.ballotInternalId && lineItem.lineItemInternalId === v.lineItemInternalId
          ? newVote
          : v
      )
    );
  }, []);

  const onFileUpload = useCallback((lineItem: StandardsBallotLineItemModel, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setEditedVotes((prev) =>
      prev.map((v) =>
        lineItem.ballotInternalId === v.ballotInternalId && lineItem.lineItemInternalId === v.lineItemInternalId
          ? { ...v, uploadedFile: files[0] }
          : v
      )
    );
  }, []);

  const validate = useCallback(() => {
    const newFieldErrors: FieldErrors[] = Array.from(editedVotes, () => ({}));

    editedVotes.forEach((v, i) => {
      if (!Validators.isNotEmpty(v.vote)) {
        newFieldErrors[i].voteSelection = Validators.requiredError;
      }
      if (!areCommentsDisabled(v) && !(Validators.isNotEmpty(v.comment) || v.uploadedFile || v.fileName)) {
        newFieldErrors[i].comments = Validators.standardsBallotsCommentsError;
      }
    });

    setFieldErrors(newFieldErrors);

    return !newFieldErrors.some((errors) => Object.keys(errors).length > 0);
  }, [editedVotes, areCommentsDisabled]);

  const saveVote = useCallback(
    async (votes: StandardsBallotsVoteModel[]) => {
      try {
        await BallotsService.SaveVotes(votes);
        refetchCycleVotes?.();
      } catch (err) {
        return Promise.reject(err);
      }
    },
    [refetchCycleVotes]
  );

  const save = useCallback(() => {
    if (!validate()) {
      setErrorMessage(Validators.formError);
      return;
    }

    setSpinnerVisible(true);
    setErrorMessage('');

    saveVote(editedVotes)
      .then(() => navigate(`${routes.standardsBallot.root}/${routes.standardsBallot.myBallots}`))
      .catch((httpError: HttpError) => {
        setSpinnerVisible(false);
        setErrorMessage(httpError.toString());
      });
  }, [editedVotes, navigate, saveVote, validate]);

  return (
    <div id="standardsBallotsFormCardComponent" className={`container ${loading ? 'emptySpacer' : ''}`}>
      <Spinner loading={loading} transparent={false} className="h-100">
        {!selectedCycle ? (
          <Alert severity="error" className="mb-3">
            Ballot does not exist
          </Alert>
        ) : (
          <CardComponent
            title={`Ballot Page For ${selectedCycle?.committee} ${selectedCycle?.period}`}
            buttonText="Submit Vote"
            onButtonClick={spinnerVisible ? undefined : save}
          >
            <div className="mb-3 description-text">
              <p>
                To review documents click{' '}
                <Link to={routes.standardsBallot.root} target="_blank" rel="noopener noreferrer">
                  here
                </Link>
                .<br />
                <i>
                  (Clicking on the link above will open the ballot page in a new tab or a new window depending on your
                  browser settings.)
                </i>
              </p>
              <div className="card-divider w-100 mb-3" />
            </div>

            {spinnerVisible ? (
              <div className="mb-4">
                <SpinnerComponent visible={spinnerVisible} />
              </div>
            ) : (
              selectedCycle?.ballotLineData?.map((item, index) => {
                const v = findLineItemVote(item, editedVotes);
                const rowKey = `${item.ballotInternalId}-${item.lineItemInternalId}`;
                return (
                  <div key={rowKey} className="row mt-1 mb-1">
                    <div className="col-md-10">
                      <CardFieldRowComponent title={`#${item.docNum}`} />
                      <CardFieldRowComponent
                        subtitle={getDocumentTitle(item)}
                        valueJSX={<div className="line-items mb-4">{item.lineItem}</div>}
                      />
                      <div className="card-divider-dashed w-100 mb-3" />

                      <div className="d-lg-none">
                        <RadioButtonComponent
                          id={`standardsballot-radiobutton-sm-${index}`}
                          radioButtons={getRadioButtonOptions(item.ballotTypeId)}
                          value={v.vote || ''}
                          onChange={(e) => updateVote(item, { ...v, vote: e.target.value })}
                          error={fieldErrors[index]?.voteSelection || ''}
                        />
                      </div>

                      <TextAreaComponent
                        id={`standardsballot-comments-${index}`}
                        label="Comments and/or Negatives:"
                        value={v.comment || ''}
                        placeholder="Enter Comments and/or Negatives here."
                        rows={3}
                        disabled={areCommentsDisabled(v)}
                        onChange={(e) => updateVote(item, { ...v, comment: e.target.value })}
                        error={areCommentsDisabled(v) ? '' : fieldErrors[index]?.comments}
                      />

                      {v.fileName && (
                        <CardFieldRowComponent
                          valueJSX={
                            <div>
                              Previously uploaded: <i>{v.fileName}</i>
                            </div>
                          }
                        />
                      )}

                      <FileComponent
                        id={`standardsballot-fileupload-${index}`}
                        label={v.fileName || ''}
                        disabled={areCommentsDisabled(v)}
                        onChange={(e) => onFileUpload(item, e)}
                        multiple={false}
                      />
                    </div>

                    <div className="col-md-2 d-none d-lg-block">
                      <RadioButtonComponent
                        id={`standardsballot-radiobutton-lg-${index}`}
                        radioButtons={getRadioButtonOptions(item.ballotTypeId)}
                        value={v.vote || ''}
                        onChange={(e) => updateVote(item, { ...v, vote: e.target.value })}
                        error={fieldErrors[index]?.voteSelection}
                      />
                    </div>
                  </div>
                );
              })
            )}

            {errorMessage && (
              <div className="form-row d-flex">
                <div className="col-12 pr-0 pl-0">
                  <div className="alert alert-danger" role="alert">
                    {errorMessage}
                  </div>
                </div>
              </div>
            )}
          </CardComponent>
        )}
      </Spinner>
    </div>
  );
};

export default StandardsBallotsFormCardComponent;
